import { useState, useCallback, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router';
import { AnimatePresence, motion } from 'motion/react';
import { X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Sidebar } from './Sidebar';
import { ChatHeader } from './ChatHeader';
import { useChatStore } from '@/stores';
import { useNewChat } from '@/hooks/useNewChat';
import { OfflineBanner } from '@/components/network';
import { AbandonConversationDialog } from './AbandonConversationDialog';

/**
 * ContentLoading - Content area loading state
 * Unlike full page LoadingPage, this component only shows in content area, keeping Sidebar visible
 */
function ContentLoading() {
  const { t } = useTranslation();

  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-violet-500" />
        <p className="mt-3 text-sm text-gray-500">{t('common:loading')}</p>
      </div>
    </div>
  );
}

/**
 * AppLayout - Main application layout
 *
 * ChatGPT style design:
 * - Fixed Sidebar on left (desktop)
 * - Drawer Sidebar on left (mobile)
 * - Main content area on right
 */
export function AppLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Chat store for cross-component state
  const { hasStarted, status } = useChatStore();

  // Unified new chat logic (with abandon confirmation)
  const {
    startNewChat,
    confirmAbandon,
    cancelAbandon,
    showAbandonDialog,
  } = useNewChat();

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Determine page title based on route and state
  const getTitle = () => {
    if (location.pathname === '/tasks') return t('sidebar:menu.tasks');
    if (location.pathname === '/settings') return t('sidebar:menu.settings');
    // Task Preview & Report pages
    if (location.pathname.match(/^\/task\/[^/]+\/report$/)) return t('tasks:report.title');
    if (location.pathname.match(/^\/task\/[^/]+$/)) return t('tasks:preview.title');
    if (hasStarted) return t('common:createTask');
    return t('sidebar:menu.home');
  };

  // Show new chat button on chat pages when conversation has started or is complete
  const isChatPage = location.pathname === '/' || location.pathname.startsWith('/c/');
  const showNewChatButton = isChatPage && (hasStarted || status === 'complete');

  return (
    <div className="flex h-screen w-full overflow-hidden bg-white">
      {/* Desktop Sidebar */}
      <div className="hidden border-r border-gray-100 md:block">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
          onCloseMobile={closeMobileMenu}
          className="h-full"
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={closeMobileMenu}
            />

            {/* Mobile Sidebar */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-y-0 left-0 z-50 w-[280px] md:hidden"
            >
              <div className="relative h-full">
                <Sidebar onCloseMobile={closeMobileMenu} className="h-full shadow-lg" />
                {/* Close button */}
                <button
                  onClick={closeMobileMenu}
                  className="absolute right-2 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Offline Banner */}
        <OfflineBanner />

        {/* Header */}
        <ChatHeader
          title={getTitle()}
          showMenuButton
          onMenuClick={toggleMobileMenu}
          onNewChat={showNewChatButton ? startNewChat : undefined}
        />

        {/* Content - with Suspense for lazy-loaded pages */}
        <div className="flex-1 overflow-hidden">
          <Suspense fallback={<ContentLoading />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Abandon Conversation Dialog */}
      <AbandonConversationDialog
        open={showAbandonDialog}
        onConfirm={confirmAbandon}
        onCancel={cancelAbandon}
      />
    </div>
  );
}
